import { useMemo, useState } from 'react';

import {
  Box,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import { Plus } from 'lucide-react';

import { ThirdPartySvg } from '@/components/common';
import { IconButton } from '@/components/core';
import { FONTS_MORE_OPACITY, FONTS_OPACITY } from '@/constant';
import { glassyEffectButton } from '@/styles/variants/glassy';

import { SuggestionItemGroup } from '../Editor';

type CommandPickerProps = {
  suggestions: SuggestionItemGroup[];
  disabled?: boolean;
  onSelect: (item: string, icon?: string) => void;
};

export function CommandPicker({
  suggestions,
  disabled = false,
  onSelect,
}: CommandPickerProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [query, setQuery] = useState('');

  const filteredGroups = useMemo(() => {
    const normalisedQuery = query.trim().toLowerCase();

    return suggestions
      .map(group => ({
        ...group,
        items: group.items.filter(item => {
          if (!normalisedQuery) return true;

          return (
            item.toLowerCase().includes(normalisedQuery) ||
            group.label.toLowerCase().includes(normalisedQuery) ||
            group.highlight.toLowerCase().includes(normalisedQuery)
          );
        }),
      }))
      .filter(group => group.items.length > 0);
  }, [query, suggestions]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelect = (item: string, icon?: string) => {
    onSelect(item, icon);
    handleClose();
  };

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={handleClose}
      placement='top-start'
      strategy='fixed'
      isLazy
      closeOnBlur
      closeOnEsc
      gutter={8}
    >
      <PopoverTrigger>
        <IconButton
          variant='glassy'
          size='sm'
          colorScheme='gray'
          zIndex={3}
          icon={<Plus size={16} />}
          disabled={disabled}
          aria-label='Insert command'
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          data-cy='composer-command-picker'
        />
      </PopoverTrigger>
      <Portal>
        <PopoverContent
          bg='gray.800'
          color='white'
          border='1px solid'
          borderColor='whiteAlpha.300'
          borderRadius='md'
          boxShadow='2xl'
          w='360px'
          maxW='calc(100vw - 16px)'
          maxH='min(420px, calc(100dvh - 8rem))'
          display='flex'
          flexDirection='column'
          overflow='hidden'
          p={0}
          _focus={{ boxShadow: 'none' }}
          _focusVisible={{ boxShadow: 'none' }}
          aria-label='Commands'
        >
          <Box px={3} pt={3} pb={2} flexShrink={0}>
            <Input
              size='sm'
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder='Type to search…'
              aria-label='Filter commands'
              bg='whiteAlpha.50'
              borderColor='whiteAlpha.200'
              _placeholder={{ color: 'whiteAlpha.600' }}
              _hover={{ borderColor: 'whiteAlpha.400' }}
              _focus={{
                borderColor: 'whiteAlpha.500',
                boxShadow: 'none',
              }}
            />
          </Box>
          <Box
            flex={1}
            minH={0}
            overflowY='auto'
            px={2}
            pb={2}
            mb='env(safe-area-inset-bottom, 0px)'
          >
            {filteredGroups.length === 0 ? (
              <Text
                fontStyle='italic'
                opacity={FONTS_MORE_OPACITY}
                textAlign='center'
                py={8}
              >
                No items found
              </Text>
            ) : (
              filteredGroups.map(group => (
                <Box key={group.label} mb={2}>
                  <Text
                    px={2}
                    pt={2}
                    pb={1}
                    fontSize='xs'
                    fontWeight='semibold'
                    letterSpacing='0.02em'
                    textTransform='uppercase'
                    opacity={FONTS_OPACITY}
                  >
                    {group.label}
                  </Text>
                  {group.items.map(item => (
                    <Box
                      key={item}
                      as='button'
                      type='button'
                      w='full'
                      display='flex'
                      alignItems='flex-start'
                      gap={3}
                      px={2}
                      py={2}
                      borderRadius='md'
                      textAlign='left'
                      cursor='pointer'
                      _hover={{ bg: 'whiteAlpha.50' }}
                      onClick={() => handleSelect(item, group.icon)}
                      data-cy='composer-command-item'
                    >
                      {group.icon ? (
                        <Box
                          display='flex'
                          alignItems='center'
                          justifyContent='center'
                          w={8}
                          h={8}
                          flexShrink={0}
                          sx={glassyEffectButton}
                          aria-hidden
                        >
                          <ThirdPartySvg src={group.icon} boxSize={4} />
                        </Box>
                      ) : null}
                      <Box minW={0}>
                        <Text fontSize='sm' noOfLines={2}>
                          {item}
                        </Text>
                        <Text
                          fontSize='xs'
                          opacity={FONTS_MORE_OPACITY}
                          noOfLines={1}
                        >
                          {group.highlight}
                        </Text>
                      </Box>
                    </Box>
                  ))}
                </Box>
              ))
            )}
          </Box>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}
